import React, { useState } from 'react';
import { Trans } from 'react-i18next';

import { AutoComplete } from '@wikifactory/voxel-ui';
import { staticURL } from 'core/url';
import { emailValidator } from 'libs/forms/validators';
import {
  Label,
  InputWrapper,
  WrappedInput,
  FormWrapper,
  FormField,
} from 'libs/forms/styles';

import { Divisor, Button } from 'components';
import { isMobile } from 'styles/utils';
import { TeamMember } from './subscriptionManager';

type Data = {
  username: string;
  id: string;
  imageFallbackChar: string;
  fullName: string;
  avatar?: string;
};

interface CreateTeamProps {
  teamName: string;
  data: Data[];
  teamMembers: TeamMember[];

  onTeamNameChange: (teamName: string) => void;
  onCreateTeamNext: () => void;
  onSearch: (name: string) => void;
  onMemberClick: (value: string) => void;
  onAddEmail: (email: string) => void;
  removeTeamMember: (name: string) => void;
}

export const CreateTeam = (props: CreateTeamProps) => {
  const {
    onCreateTeamNext,
    teamName,
    onTeamNameChange,
    teamMembers,
    removeTeamMember,
    onAddEmail,
  } = props;
  const [isEmail, setIsEmail] = useState(false);
  const [chooseUsername, setChooseUsername] = useState('');

  return (
    <div className='flex-col mt-8 w-full px-8'>
      <h1 className='text-center'>Create a Team</h1>
      <FormWrapper
        noPadding
        responsive
        onSubmit={e => {
          e.preventDefault();
        }}
        maxResponsive
      >
        <FormField>
          <Label bold>
            <Trans>Team Name</Trans>
          </Label>
          <InputWrapper padding='7px 10px' responsive>
            <WrappedInput
              onChange={(e: any) => {
                onTeamNameChange(e.target.value);
              }}
              data-testid='createTeamPage-teamName-input'
              value={teamName}
              placeholder='Team Name'
              autoFocus
            />
          </InputWrapper>
        </FormField>
        {!isMobile() && (
          <React.Fragment>
            <Divisor mt={4} />
            <div className='mt-4 flex items-center'>
              <h2 className='font-extrabold text-black'>
                Invite Collaborators
              </h2>
              <img
                className='mx-2'
                src={staticURL('assets/icons/invite.svg')}
              />
              <span
                className='text-xs align-bottom text-black'
                data-testid='createTeamPage-memberNumber-text'
              >{`(${teamMembers?.length ?? 0}x)`}</span>
            </div>
            <div className='flex flex-wrap' style={{ maxWidth: '436px' }}>
              {teamMembers?.map((member: TeamMember) => {
                const showValue = member.username || member.email;

                return (
                  <div key={showValue} className='mr-2 mt-2'>
                    <Button
                      dataTestId={`createTeamPage-${showValue}-button`}
                      text={showValue}
                      variant='secondary'
                      outline
                      size='xsmall'
                      icon='close'
                      iconPosition='right'
                      onIconClick={() => {
                        removeTeamMember(showValue);
                      }}
                    />
                  </div>
                );
              })}
            </div>

            <div className='mt-4'>
              <AutoComplete
                value={chooseUsername}
                inputValue={chooseUsername}
                noOptionsText='Not match to any collaborators'
                options={
                  props.data?.map(d => {
                    return {
                      label: d.username,
                    };
                  }) ?? []
                }
                onChange={(
                  e: React.SyntheticEvent,
                  option: { label: string },
                ) => {
                  if (option?.label) {
                    props.onMemberClick(option.label);
                    setChooseUsername('');
                  }
                }}
                textProps={{
                  'data-testid': 'createTeamPage-autoComplete-input',
                  label: isEmail
                    ? 'Enter/Tab to add this email'
                    : 'Type an Email or search by Username and Name',
                  onKeyDown: (
                    e: React.KeyboardEvent<HTMLDivElement>,
                    value: string,
                    params: Record<string, any>,
                  ) => {
                    if (
                      ['Enter', 'Tab'].includes(e.key) &&
                      emailValidator(value)
                    ) {
                      onAddEmail(value);
                      setIsEmail(false);
                      // clear input value after press enter/tab key
                      params?.inputProps?.onBlur?.();
                      params?.inputProps?.onFocus?.();
                      return true;
                    }
                  },
                  onKeyPress: (e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === ' ') {
                      e.preventDefault();
                    }
                  },
                  onChange: (
                    e: React.ChangeEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => {
                    setIsEmail(emailValidator(e.target.value?.trim()));
                    if (props.onSearch) {
                      props.onSearch(e.target.value?.trim());
                    }
                  },
                }}
              />
            </div>
          </React.Fragment>
        )}
      </FormWrapper>

      <div className='flex justify-center mt-8'>
        <Button
          text='Next'
          dataTestId='createTeamPage-next-button'
          variant='tertiary'
          icon='arrow_right'
          iconPosition='right'
          onClick={() => {
            if (onCreateTeamNext) {
              onCreateTeamNext();
            }
          }}
        />
      </div>
    </div>
  );
};
